import {
  Box, CircularProgress, IconButton, InputAdornment, Menu, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { icons } from 'constants/images'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TargetsApi from 'api/executive/targets.api'
import { useBranches, useProcedures } from 'features/executive/shared/hooks/use-reference-data'
import type { ProcedureTarget } from 'api/executive/executive.types'
import { MONTH_SHORT_TH, toBuddhistYear, toGregorianYear } from 'utils/executive.utils'
import { numberFormat } from 'utils/app.utils'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextField from 'component/Input/InputTextField'
import PageModal from 'new-components/modal/page-modal'
import SelectInput from 'new-components/inputs/select-input'
import ProcedurePickerModal from './ProcedurePickerModal'
import TabLoading from './TabLoading'
import { notiSuccess, notiError } from 'component/notifications/notifications'

type Cell = {
  procedureId: number
  branchId: number
  caseCount: number | null
  targetId?: number
}

/** Calendar arithmetic for the copy-from-month feature. */
function computePrevMonth(beYear: number, month: number): { beYear: number; month: number } {
  if (month === 1) return { beYear: beYear - 1, month: 12 }
  return { beYear, month: month - 1 }
}

/**
 * Watchlist UX — the clinic has ~1,200 operatives but only tracks a few
 * strategic procedures (Invisalign, Implant, Crown, …). Rendering 1,200 ×
 * 11 branches × 12 months = 158,400 inputs is unusable. Instead:
 *
 *  - Show only procedures that have ≥1 saved target row, OR were added in
 *    this session via the Autocomplete picker
 *  - Adding a procedure inserts an empty row; saving with values > 0
 *    persists it
 *  - The × button stages a row for deletion; saving sends DELETE calls
 *    for its target IDs in this month
 */
export default function ProcedureTargetsTab() {
  const { t } = useTranslation()
  const now = new Date()
  const [beYear, setBeYear] = useState(() => toBuddhistYear(now.getFullYear()))
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [cells, setCells] = useState<Cell[]>([])
  const [search, setSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  /** Anchor element for the "คัดลอก..." dropdown menu. null = closed. */
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  /** Open state for the custom year/month picker dialog. */
  const [pickDialogOpen, setPickDialogOpen] = useState(false)
  /** When non-null, the unsaved-changes confirm dialog is open for this source. */
  const [confirmCopy, setConfirmCopy] = useState<{ beYear: number; month: number } | null>(null)
  /** Selected values inside the picker dialog. */
  const [pickYear, setPickYear] = useState<number>(toBuddhistYear(new Date().getFullYear()))
  const [pickMonth, setPickMonth] = useState<number>(new Date().getMonth() + 1)
  /** Months (1-12) that have at least one saved target in pickYear.
   *  Populated when the picker dialog opens and refreshed when pickYear
   *  changes — the dropdown filters to these months only, so the user
   *  can't pick a source month that has nothing to copy from. */
  const [monthsWithData, setMonthsWithData] = useState<Set<number>>(new Set())
  const [monthsLoading, setMonthsLoading] = useState(false)
  /** Procedure IDs the user has explicitly added this session but haven't saved yet. */
  const [adhocIds, setAdhocIds] = useState<number[]>([])
  /** Procedure IDs the user has marked for removal — stored target IDs to DELETE on save. */
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([])
  /** Snapshot of caseCount keyed by `${procedureId}-${branchId}` at the last
   *  successful load(). hasUnsavedChanges compares the live `cells` against
   *  this to detect per-cell edits (the adhoc/pending flags alone miss
   *  value changes on already-saved procedures). */
  const [baseline, setBaseline] = useState<Map<string, number | null>>(new Map())
  /** When the user changes Year/Month while hasUnsavedChanges is true, hold
   *  the target here and open a confirm dialog before applying. Cancel
   *  leaves the dropdowns on the current beYear/month; confirm applies the
   *  pending target (the [beYear, month] effects then reset adhoc state and
   *  reload from DB, discarding the user's in-flight edits). */
  const [pendingMonthChange, setPendingMonthChange] = useState<{ beYear: number; month: number } | null>(null)
  /** Open state for the "reset all unsaved edits" confirm dialog. */
  const [confirmReset, setConfirmReset] = useState(false)

  const { data: branches, loading: branchesLoading } = useBranches({ scope: 'all-branches' })
  const { data: procedures, loading: proceduresLoading } = useProcedures()

  const procedureById = useMemo(() => {
    const m = new Map<number, { id: number; name: string; code?: string }>()
    procedures.forEach((p) => m.set(p.id, p))
    return m
  }, [procedures])

  const load = async (opts?: { excludeAdhoc?: boolean }) => {
    if (branches.length === 0) return
    setLoading(true)
    try {
      const res = await TargetsApi.getProcedureTargets({
        year: toGregorianYear(beYear),
        month,
      })
      const byKey = new Map<string, ProcedureTarget>()
      const savedIds = new Set<number>()
      res.items.forEach((it) => {
        byKey.set(`${it.procedureId}-${it.branchId}`, it)
        savedIds.add(it.procedureId)
      })
      // Watchlist = procedures with saved rows + any ad-hoc ones, minus pending deletes.
      // After a successful save we deliberately drop adhoc-only rows so the
      // table reflects what's actually in the DB.
      const watchlist = new Set<number>([
        ...Array.from(savedIds),
        ...(opts?.excludeAdhoc ? [] : adhocIds),
      ])
      pendingDeleteIds.forEach((id) => watchlist.delete(id))

      const next: Cell[] = []
      for (const procId of Array.from(watchlist)) {
        for (const b of branches) {
          const existing = byKey.get(`${procId}-${b.id}`)
          next.push({
            procedureId: procId,
            branchId: b.id,
            caseCount: existing?.caseCount ?? null,
            targetId: existing?.id,
          })
        }
      }
      setCells(next)
      // Snapshot baseline at the moment of a successful load so
      // hasUnsavedChanges can detect per-cell value edits made afterwards.
      setBaseline(new Map(next.map((c) => [`${c.procedureId}-${c.branchId}`, c.caseCount])))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() /* eslint-disable-line */ }, [beYear, month, branches])

  // Reset per-session state when month/year changes (watchlist comes from DB)
  useEffect(() => {
    setAdhocIds([])
    setPendingDeleteIds([])
  }, [beYear, month])

  // Each time the picker dialog opens, seed its dropdowns with prev month
  // so the happy path is one click away.
  useEffect(() => {
    if (pickDialogOpen) {
      const prev = computePrevMonth(beYear, month)
      setPickYear(prev.beYear)
      setPickMonth(prev.month)
    }
  }, [pickDialogOpen, beYear, month])

  // Fetch the set of months that actually have data for pickYear whenever
  // the picker dialog opens or the user changes pickYear. The month
  // dropdown filters to this set so the user can only choose source
  // months that hold at least one saved target.
  useEffect(() => {
    if (!pickDialogOpen) return
    let cancelled = false
    setMonthsLoading(true)
    TargetsApi.getProcedureTargets({ year: toGregorianYear(pickYear) })
      .then((res) => {
        if (cancelled) return
        const months = new Set<number>()
        res.items.forEach((it) => { if (it.month != null) months.add(it.month) })
        setMonthsWithData(months)
        // Auto-correct pickMonth to the latest available month if the
        // current selection isn't in the data.
        if (months.size > 0 && !months.has(pickMonth)) {
          const sorted = Array.from(months).sort((a, b) => a - b)
          setPickMonth(sorted[sorted.length - 1])
        }
      })
      .catch(() => { if (!cancelled) setMonthsWithData(new Set()) })
      .finally(() => { if (!cancelled) setMonthsLoading(false) })
    return () => { cancelled = true }
    // pickMonth omitted on purpose: only re-fetch when dialog opens or year changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickDialogOpen, pickYear])

  const updateCell = (procedureId: number, branchId: number, caseCount: number | null) => {
    setCells((prev) => {
      const next = prev.slice()
      const i = next.findIndex((c) => c.procedureId === procedureId && c.branchId === branchId)
      if (i >= 0) {
        next[i] = { ...next[i], caseCount }
      } else {
        next.push({ procedureId, branchId, caseCount })
      }
      return next
    })
  }

  /** Set every branch cell of a procedure to the same value. */
  const setAllBranches = (procedureId: number, caseCount: number | null) => {
    setCells((prev) =>
      prev.map((c) =>
        c.procedureId === procedureId ? { ...c, caseCount } : c,
      ),
    )
  }

  const addProcedure = (procedureId: number) => {
    // Already in watchlist?
    if (cells.some((c) => c.procedureId === procedureId)) return
    setAdhocIds((prev) => prev.includes(procedureId) ? prev : [...prev, procedureId])
    // Seed empty cells for each branch
    setCells((prev) => [
      ...prev,
      ...branches.map((b) => ({ procedureId, branchId: b.id, caseCount: null as number | null })),
    ])
  }

  const removeProcedure = (procedureId: number) => {
    setPendingDeleteIds((prev) => prev.includes(procedureId) ? prev : [...prev, procedureId])
    setAdhocIds((prev) => prev.filter((id) => id !== procedureId))
    setCells((prev) => prev.filter((c) => c.procedureId !== procedureId))
  }

  const save = async () => {
    setSaving(true)
    try {
      // DELETE removed procedures' targets for this month
      const deletions: Promise<unknown>[] = []
      if (pendingDeleteIds.length > 0) {
        const targetIds = await TargetsApi.getProcedureTargets({
          year: toGregorianYear(beYear),
          month,
        }).then((r) => r.items.filter((it) => pendingDeleteIds.includes(it.procedureId)).map((it) => it.id))
        targetIds.forEach((id) => deletions.push(TargetsApi.deleteProcedureTarget(id)))
      }
      await Promise.all(deletions)

      // UPSERT remaining cells. Send (a) explicit positive values, and
      // (b) every cell of session-added (adhoc) procedures so they get
      // persisted to DB even when the user added them without entering
      // numbers yet — otherwise the load() refresh below drops them.
      const adhocSet = new Set(adhocIds)
      const payload = cells
        .filter((c) =>
          (c.caseCount != null && c.caseCount > 0) ||
          adhocSet.has(c.procedureId),
        )
        .map((c) => ({
          id: c.targetId,
          procedureId: c.procedureId,
          branchId: c.branchId,
          year: toGregorianYear(beYear),
          month,
          caseCount: c.caseCount ?? 0,
        }))
      if (payload.length > 0) {
        await TargetsApi.upsertProcedureTargets({ items: payload as any })
      }

      setPendingDeleteIds([])
      setAdhocIds([])
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
      await load({ excludeAdhoc: true })
    } catch {
      notiError(t('DASHBOARD_TARGETS.SAVE_ERROR') as string)
    } finally {
      setSaving(false)
    }
  }

  /** Discard every in-progress edit and reload the current month from DB.
   *  Same end state as if the user had just opened the tab on this month. */
  const doReset = async () => {
    setAdhocIds([])
    setPendingDeleteIds([])
    await load({ excludeAdhoc: true })
    notiSuccess(t('DASHBOARD_TARGETS.RESET_SUCCESS', { defaultValue: 'รีเซ็ตการแก้ไขแล้ว' }) as string)
  }

  /** Fetch a source month and REPLACE current cells/adhoc with its content. */
  const doCopy = async (srcBeYear: number, srcMonth: number) => {
    if (srcBeYear === beYear && srcMonth === month) {
      notiError(t('DASHBOARD_TARGETS.COPY_SAME_MONTH', {
        defaultValue: 'เดือนต้นทางและปลายทางเป็นเดือนเดียวกัน',
      }) as string)
      return
    }
    setLoading(true)
    try {
      const res = await TargetsApi.getProcedureTargets({
        year: toGregorianYear(srcBeYear),
        month: srcMonth,
      })
      if (res.items.length === 0) {
        notiError(t('DASHBOARD_TARGETS.COPY_EMPTY', {
          defaultValue: 'ไม่มีข้อมูลในเดือนนั้น',
        }) as string)
        return
      }
      // Preserve destination-month targetIds so save() updates existing rows
      // instead of creating duplicates. Without this, copying twice would
      // double the DB rows for any (procedure, branch) already saved here.
      const existingTargetIds = new Map<string, number>()
      cells.forEach((c) => {
        if (c.targetId != null) existingTargetIds.set(`${c.procedureId}-${c.branchId}`, c.targetId)
      })

      const newCells: Cell[] = res.items.map((it) => ({
        procedureId: it.procedureId,
        branchId: it.branchId,
        caseCount: it.caseCount,
        targetId: existingTargetIds.get(`${it.procedureId}-${it.branchId}`),
      }))
      const newAdhocIds = Array.from(new Set(newCells.map((c) => c.procedureId)))
      setCells(newCells)
      setAdhocIds(newAdhocIds)
      setPendingDeleteIds([])
      notiSuccess(t('DASHBOARD_TARGETS.COPY_SUCCESS', {
        defaultValue: 'คัดลอกแล้ว {{n}} รายการ — กดบันทึกเพื่อยืนยัน',
        n: newAdhocIds.length,
      }) as string)
    } catch {
      notiError(t('DASHBOARD_TARGETS.COPY_ERROR', {
        defaultValue: 'คัดลอกไม่สำเร็จ',
      }) as string)
    } finally {
      setLoading(false)
    }
  }

  const rowTotals = useMemo(() => {
    const byProcedure = new Map<number, number>()
    cells.forEach((c) => {
      if (c.caseCount != null) {
        byProcedure.set(c.procedureId, (byProcedure.get(c.procedureId) ?? 0) + c.caseCount)
      }
    })
    return byProcedure
  }, [cells])

  /** Per-branch totals across every procedure in the watchlist. Feeds the
   *  "รวมทุกสาขา" footer row at the bottom of the table. */
  const branchTotals = useMemo(() => {
    const m = new Map<number, number>()
    cells.forEach((c) => {
      if (c.caseCount != null) m.set(c.branchId, (m.get(c.branchId) ?? 0) + c.caseCount)
    })
    return m
  }, [cells])

  /** Grand total = sum of every caseCount in the watchlist. */
  const grandTotal = useMemo(() => {
    let sum = 0
    cells.forEach((c) => { if (c.caseCount != null) sum += c.caseCount })
    return sum
  }, [cells])

  const cellMap = useMemo(() => {
    const m = new Map<string, Cell>()
    cells.forEach((c) => m.set(`${c.procedureId}-${c.branchId}`, c))
    return m
  }, [cells])

  /** For each procedure: the shared value across branches, or 'mixed' when branches differ. */
  const allBranchValues = useMemo(() => {
    const grouped = new Map<number, (number | null)[]>()
    cells.forEach((c) => {
      const arr = grouped.get(c.procedureId) ?? []
      arr.push(c.caseCount)
      grouped.set(c.procedureId, arr)
    })
    const m = new Map<number, number | null | 'mixed'>()
    grouped.forEach((vals, pid) => {
      const first = vals[0]
      m.set(pid, vals.every((v) => v === first) ? first : 'mixed')
    })
    return m
  }, [cells])

  // Visible procedures = watchlist (those represented in cells), with optional search filter.
  // Sort key matches the picker modal: code natural (numeric-aware) so "01-2"
  // precedes "01-11"; fall back to name when a procedure has no code.
  const visibleProcedures = useMemo(() => {
    const inWatchlist = Array.from(new Set(cells.map((c) => c.procedureId)))
    const items = inWatchlist
      .map((id) => procedureById.get(id) ?? { id, name: `#${id}`, code: undefined as string | undefined })
    const q = search.trim().toLowerCase()
    return (q ? items.filter((p) => p.name.toLowerCase().includes(q)) : items)
      .sort((a, b) => {
        const ka = a.code || a.name
        const kb = b.code || b.name
        return ka.localeCompare(kb, undefined, { numeric: true, sensitivity: 'base' })
      })
  }, [cells, procedureById, search])

  /** True when the watchlist composition changed (add/remove) OR any cell's
   *  caseCount differs from the value captured at the last load(). The
   *  adhoc/pending flags alone miss value edits on already-saved procedures. */
  const hasUnsavedChanges = useMemo(() => {
    if (adhocIds.length > 0 || pendingDeleteIds.length > 0) return true
    for (const c of cells) {
      if (baseline.get(`${c.procedureId}-${c.branchId}`) !== c.caseCount) return true
    }
    return false
  }, [cells, baseline, adhocIds, pendingDeleteIds])

  return (
    <Box>
      {/*
        Every save commits exactly one (year, month) — the one shown in the
        toolbar at the moment of click. To make that invariant robust against
        race conditions (e.g. month change during an in-flight load/save,
        adding a procedure while a save is mid-flight), every control that
        could mutate the source/destination context is disabled whenever
        `saving || loading` is true.
      */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2">{t('DASHBOARD_TARGETS.YEAR')}</Typography>
        <Select
          size="small"
          value={beYear}
          onChange={(e) => {
            const next = Number(e.target.value)
            if (next === beYear) return
            if (hasUnsavedChanges) setPendingMonthChange({ beYear: next, month })
            else setBeYear(next)
          }}
          disabled={saving || loading}
        >
          {[beYear - 1, beYear, beYear + 1].map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </Select>
        <Typography variant="body2">{t('DASHBOARD_TARGETS.MONTH')}</Typography>
        <Select
          size="small"
          value={month}
          onChange={(e) => {
            const next = Number(e.target.value)
            if (next === month) return
            if (hasUnsavedChanges) setPendingMonthChange({ beYear, month: next })
            else setMonth(next)
          }}
          disabled={saving || loading}
        >
          {MONTH_SHORT_TH.map((m, i) => (
            <MenuItem key={i} value={i + 1}>{m}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder={t('DASHBOARD_TARGETS.SEARCH_PROCEDURE', 'กรองรายการที่แสดง') as string}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <ButtonCustom
          variant="outlined"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={proceduresLoading || saving || loading}
          textButton={t('DASHBOARD_TARGETS.COPY_BUTTON', { defaultValue: 'คัดลอก...' })}
        />
        <ButtonCustom
          mode="add"
          variant="contained"
          onClick={() => setPickerOpen(true)}
          disabled={proceduresLoading || saving || loading}
          textButton={t('DASHBOARD_TARGETS.ADD_PROCEDURE_BUTTON', { defaultValue: '+ เพิ่มหัตถการ' })}
        />
        <ButtonCustom
          variant="outlined"
          onClick={() => setConfirmReset(true)}
          disabled={saving || loading || !hasUnsavedChanges}
          textButton={t('DASHBOARD_TARGETS.RESET_BUTTON', { defaultValue: 'รีเซ็ต' })}
        />
        <ButtonCustom
          variant="contained"
          onClick={save}
          disabled={saving || loading || !hasUnsavedChanges}
          textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
        />
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {(() => {
          const prev = computePrevMonth(beYear, month)
          const prevLabel = `${MONTH_SHORT_TH[prev.month - 1]} ${prev.beYear}`
          const handle = (target: { beYear: number; month: number }) => {
            setMenuAnchor(null)
            if (hasUnsavedChanges) setConfirmCopy(target)
            else doCopy(target.beYear, target.month)
          }
          return [
            <MenuItem key="prev" onClick={() => handle(prev)}>
              {t('DASHBOARD_TARGETS.COPY_PREV_MONTH', {
                defaultValue: 'เดือนก่อนหน้า ({{m}})',
                m: prevLabel,
              })}
            </MenuItem>,
            <MenuItem
              key="pick"
              onClick={() => {
                setMenuAnchor(null)
                setPickDialogOpen(true)
              }}
            >
              {t('DASHBOARD_TARGETS.COPY_PICK_MONTH', { defaultValue: 'เลือกเดือนอื่น...' })}
            </MenuItem>,
          ]
        })()}
      </Menu>

      <PageModal
        isOpen={pickDialogOpen}
        size="XS"
        title={t('DASHBOARD_TARGETS.COPY_PICK_TITLE', { defaultValue: 'คัดลอกข้อมูล' }) as string}
        handleClose={() => setPickDialogOpen(false)}
        onCancel={{
          onClick: () => setPickDialogOpen(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setPickDialogOpen(false)
            const target = { beYear: pickYear, month: pickMonth }
            if (hasUnsavedChanges) setConfirmCopy(target)
            else doCopy(target.beYear, target.month)
          },
          title: t('DASHBOARD_TARGETS.COPY_BUTTON_SHORT', { defaultValue: 'คัดลอก' }) as string,
          disabled: loading || monthsLoading || monthsWithData.size === 0,
        }}
        overflow
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {t('DASHBOARD_TARGETS.YEAR')}
            </Typography>
            <SelectInput
              value={pickYear}
              onChange={(e) => setPickYear(Number(e.target.value))}
              options={[pickYear - 1, pickYear, pickYear + 1].map((y) => ({ value: y, name: y }))}
              width={120}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {t('DASHBOARD_TARGETS.MONTH')}
            </Typography>
            <SelectInput
              value={monthsWithData.has(pickMonth) ? pickMonth : ''}
              onChange={(e) => setPickMonth(Number(e.target.value))}
              options={MONTH_SHORT_TH
                .map((m, i) => ({ value: i + 1, name: m }))
                .filter((o) => monthsWithData.has(o.value))}
              width={120}
              loading={monthsLoading}
              disabled={!monthsLoading && monthsWithData.size === 0}
            />
          </Box>
        </Box>
        {!monthsLoading && monthsWithData.size === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('DASHBOARD_TARGETS.COPY_NO_DATA_IN_YEAR', {
              defaultValue: 'ไม่มีข้อมูลในปี {{y}} — เลือกปีอื่น',
              y: pickYear,
            })}
          </Typography>
        )}
      </PageModal>

      <PageModal
        isOpen={Boolean(confirmCopy)}
        size="XS"
        title={t('DASHBOARD_TARGETS.COPY_OVERWRITE_TITLE', {
          defaultValue: 'ทับการเปลี่ยนแปลงที่ยังไม่บันทึก?',
        }) as string}
        handleClose={() => setConfirmCopy(null)}
        onCancel={{
          onClick: () => setConfirmCopy(null),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            if (confirmCopy) {
              const target = confirmCopy
              setConfirmCopy(null)
              doCopy(target.beYear, target.month)
            }
          },
          title: t('DASHBOARD_TARGETS.COPY_OVERWRITE_CONFIRM', {
            defaultValue: 'ดำเนินการต่อ',
          }) as string,
          type: 'danger',
          disabled: loading,
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.COPY_OVERWRITE_BODY', {
            defaultValue: 'การคัดลอกจะแทนที่ข้อมูลปัจจุบันทั้งหมด รวมถึงรายการที่ยังไม่บันทึก',
          })}
        </Typography>
      </PageModal>

      <PageModal
        isOpen={confirmReset}
        size="XS"
        title={t('DASHBOARD_TARGETS.RESET_TITLE', { defaultValue: 'ยืนยันรีเซ็ตการแก้ไข' }) as string}
        handleClose={() => setConfirmReset(false)}
        onCancel={{
          onClick: () => setConfirmReset(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setConfirmReset(false)
            doReset()
          },
          title: t('DASHBOARD_TARGETS.RESET_CONFIRM', { defaultValue: 'รีเซ็ต' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.RESET_BODY', {
            defaultValue: 'หมายเหตุ: การเปลี่ยนแปลงที่ยังไม่บันทึกทั้งหมดจะถูกรีเซ็ต',
          })}
        </Typography>
      </PageModal>

      <PageModal
        isOpen={Boolean(pendingMonthChange)}
        size="XS"
        title={t('DASHBOARD_TARGETS.MONTH_CHANGE_TITLE', { defaultValue: 'ยืนยันเปลี่ยนเดือน' }) as string}
        handleClose={() => setPendingMonthChange(null)}
        onCancel={{
          onClick: () => setPendingMonthChange(null),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            if (pendingMonthChange) {
              setBeYear(pendingMonthChange.beYear)
              setMonth(pendingMonthChange.month)
              setPendingMonthChange(null)
            }
          },
          title: t('DASHBOARD_TARGETS.MONTH_CHANGE_CONFIRM', { defaultValue: 'ยืนยัน' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.MONTH_CHANGE_BODY', {
            defaultValue: 'หมายเหตุ: การเปลี่ยนแปลงที่ยังไม่บันทึกจะถูกรีเซ็ตทั้งหมด',
          })}
        </Typography>
      </PageModal>

      {loading || branchesLoading ? (
        <TabLoading />
      ) : visibleProcedures.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t('DASHBOARD_TARGETS.EMPTY_WATCHLIST', 'ยังไม่มีหัตถการในรายการ — ใช้ช่องค้นหาด้านบนเพื่อเพิ่ม')}
          </Typography>
        </Paper>
      ) : (
        // maxHeight makes the Paper itself the vertical scroll container,
        // which is what stickyHeader needs to anchor against.
        <Paper sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 280, position: 'sticky', left: 0, zIndex: 3 }}>
                  {t('EXECUTIVE.FILTER.PROCEDURE')}
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 90, fontWeight: 600 }}>
                  {t('DASHBOARD_TARGETS.ALL_BRANCHES_INPUT', { defaultValue: 'ทุกสาขา' })}
                </TableCell>
                {branches.map((b) => (
                  <TableCell key={b.id} align="right">{b.name}</TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {t('DASHBOARD_TARGETS.TOTAL')}
                </TableCell>
                <TableCell padding="none" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleProcedures.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ minWidth: 280, maxWidth: 360, position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      {p.code && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', minWidth: 56, mt: 0.25 }}
                        >
                          {p.code}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                          lineHeight: 1.35,
                          wordBreak: 'break-word',
                        }}
                      >
                        {p.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  {(() => {
                    const v = allBranchValues.get(p.id)
                    const isMixed = v === 'mixed'
                    return (
                      <TableCell align="right" sx={{ backgroundColor: 'action.hover' }}>
                        <InputTextField
                          hookValue
                          inputHeight={32}
                          type="number"
                          inputTextAlign="right"
                          placeholder={isMixed ? '—' : '0'}
                          value={isMixed ? '' : (v ?? '')}
                          onchange={(e: any) =>
                            setAllBranches(p.id, e.target.value === '' ? null : Number(e.target.value))
                          }
                          inputProps={{ min: 0 }}
                          style={{ width: 140 }}
                        />
                      </TableCell>
                    )
                  })()}
                  {branches.map((b) => {
                    const cell = cellMap.get(`${p.id}-${b.id}`)
                    return (
                      <TableCell key={b.id} align="right">
                        <InputTextField
                          hookValue
                          inputHeight={32}
                          type="number"
                          inputTextAlign="right"
                          placeholder="0"
                          value={cell?.caseCount ?? ''}
                          onchange={(e: any) =>
                            updateCell(p.id, b.id, e.target.value === '' ? null : Number(e.target.value))
                          }
                          inputProps={{ min: 0 }}
                          style={{ width: 140 }}
                        />
                      </TableCell>
                    )
                  })}
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {numberFormat(rowTotals.get(p.id) ?? 0)}
                  </TableCell>
                  <TableCell padding="none">
                    <Tooltip title={t('DASHBOARD_TARGETS.REMOVE_PROCEDURE', 'ลบจากรายการ') as string}>
                      <IconButton size="small" onClick={() => removeProcedure(p.id)}>
                        <img src={icons.btnTrash} alt="delete" style={{ width: 20, height: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/*
                Totals footer row: sums across every procedure in the watchlist.
                "ทุกสาขา" column is intentionally blank (per-branch aggregate
                has no shared scalar to display); branch columns show the
                per-branch sum; right edge shows the grand total.
              */}
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {t('DASHBOARD_TARGETS.ALL_BRANCHES')}
                </TableCell>
                <TableCell />
                {branches.map((b) => (
                  <TableCell key={b.id} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {numberFormat(branchTotals.get(b.id) ?? 0)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {numberFormat(grandTotal)}
                </TableCell>
                <TableCell padding="none" />
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('DASHBOARD_TARGETS.WATCHLIST_COUNT', { defaultValue: 'รายการที่ติดตาม: {{n}}', n: visibleProcedures.length })}
          {hasUnsavedChanges && ` · ${t('DASHBOARD_TARGETS.UNSAVED', 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก')}`}
        </Typography>
      </Box>
      <ProcedurePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        procedures={procedures}
        alreadyAddedIds={Array.from(new Set(cells.map((c) => c.procedureId)))}
        onConfirm={(ids) => {
          ids.forEach((id) => addProcedure(id))
          setPickerOpen(false)
        }}
      />
    </Box>
  )
}
